import { i18n } from '@lingui/core';
import { en, pt } from 'make-plural/plurals';
import { messages as enMessages } from './locales/en/messages';
import { messages as ptBRMessages } from './locales/pt-BR/messages';

// Define the available locales and their messages
export const locales = {
  en: 'English',
  'pt-BR': 'Português (Brasil)',
};

// Register the plurals
i18n.loadLocaleData({
  en: { plurals: en },
  'pt-BR': { plurals: pt },
});

// Function to activate a locale
export function activateLocale(locale: string) {
  // Load messages for the locale
  let messages;
  switch (locale) {
    case 'en':
      messages = enMessages;
      break;
    case 'pt-BR':
      messages = ptBRMessages;
      break;
    default:
      messages = enMessages;
  }

  i18n.load(locale, messages);
  i18n.activate(locale);

  // Update HTML lang attribute for SEO
  // For pt-BR, use 'pt' as the primary language tag with 'BR' as the region subtag
  const htmlLang = locale === 'pt-BR' ? 'pt-BR' : locale;
  document.documentElement.setAttribute('lang', htmlLang);

  // Update meta description based on language
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    if (locale === 'pt-BR') {
      metaDescription.setAttribute('content', 'OTS_Analytics - Estatísticas e análises de serveridores Open Tibia');
      document.title = 'OTS_Analytics - Estatísticas de Servidores';
    } else {
      metaDescription.setAttribute('content', 'OTS_Analytics - Server statistics and analytics for Open Tibia Servers');
      document.title = 'OTS_Analytics - Server Statistics';
    }
  }

  // Store the selected locale in localStorage
  localStorage.setItem('locale', locale);

  return locale;
}

// Detect browser language
function detectBrowserLanguage(): string {
  // Default locale if detection fails
  const defaultLocale = 'en';

  try {
    // Get browser language preferences
    const browserLang = navigator.language || (navigator as any).userLanguage || '';

    // Check if the browser language matches any of our supported locales
    if (browserLang.startsWith('pt')) {
      return 'pt-BR';
    } else if (browserLang.startsWith('en')) {
      return 'en';
    }

    // If no match, return default
    return defaultLocale;
  } catch (error) {
    console.error('Error detecting browser language:', error);
    return defaultLocale;
  }
}

// Initialize i18n with the locale from: localStorage (if exists) -> browser language -> default
export function initI18n() {
  const defaultLocale = 'en';

  // Priority: 1. localStorage (user preference), 2. Browser language, 3. Default locale
  const savedLocale = localStorage.getItem('locale');
  const browserLocale = detectBrowserLanguage();

  // Use saved locale if available, otherwise use browser locale
  const localeToUse = savedLocale || browserLocale || defaultLocale;

  return activateLocale(localeToUse);
}

// Get the current locale
export function getCurrentLocale() {
  return i18n.locale;
}

// Get all available locales
export function getAvailableLocales() {
  return Object.keys(locales);
}

// Check if a locale is supported
export function isSupportedLocale(locale: string): boolean {
  return Object.keys(locales).includes(locale);
}

export { i18n };
